'use client'

/**
 * Saved + Compare — the header PANELS and the row renderer. The store (hook,
 * localStorage keys, compareHref) lives in ./saved-compare-store.ts and is
 * re-exported here so product cards, marquees and the saved page keep their
 * imports. The site header renders its two buttons from
 * components/chrome/header-saved-compare.tsx and lazy-loads this file the
 * first time a panel opens.
 */
import Link from 'next/link'
import * as React from 'react'
import { SafeImage } from '@/components/ui/safe-image'
import { formatPrice } from './format'
import { useSavedCompare, compareHref, COMPARE_MAX, type SavedItem } from './saved-compare-store'

export { useSavedCompare, compareHref, type SavedItem } from './saved-compare-store'


function PanelRow({ item, onRemove, showLink }: { item: SavedItem; onRemove: () => void; showLink: boolean }) {
  return (
    <div className="hdrpanel-row">
      <span className="hdrpanel-th" role="img" aria-label={item.title}>
        <SafeImage src={item.image} alt="" width={56} height={56} className="object-contain w-full h-full" />
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
        {item.brand ? <span className="pbrand">{item.brand}</span> : null}
        <span className="hdrpanel-t">{item.title}</span>
        {item.fin ? (
          <span className="pmodel">
            <span className="idchip">{item.fin}</span>
          </span>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flex: 'none' }}>
        {Number.isFinite(item.priceCents) && item.priceCents > 0 ? <><span className="text-xs text-gray-600">{showLink ? 'Price when saved' : 'Price when added'}</span><span className="pprice">{formatPrice(item.priceCents)}</span></> : null}
        {showLink ? (
          <Link className="hdrpanel-go" href={item.href} aria-label={`Check current price for ${item.title}`}>
            Check current price
          </Link>
        ) : null}
      </div>
      <button type="button" className="hdrpanel-rm" onClick={onRemove} aria-label="Remove">
        ✕
      </button>
    </div>
  )
}

/**
 * The two header buttons with live counts and their panels. Mount once in the
 * site header, inside any `position: relative` container; the panel positions
 * itself under the header row.
 */
/** One open panel — saved or compare — rendered under the header buttons. */
export function SavedComparePanel({ kind, onClose }: { kind: 'sav' | 'cmp'; onClose: () => void }) {
  const sc = useSavedCompare()
  return (
    <>
      {kind === 'sav' ? (
        <div className="hdrpanel" role="dialog" aria-label="Saved products">
          <div className="hdrpanel-h">
            <span>
              Saved <span className="hdrpanel-n">{sc.saved.length}</span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
              {sc.saved.length ? (
                <button type="button" className="hdrpanel-clr" onClick={() => sc.clearSaved()}>
                  Clear all
                </button>
              ) : null}
              <button type="button" className="hdrpanel-x" onClick={onClose} aria-label="Close">
                ✕
              </button>
            </span>
          </div>
          {sc.saved.length === 0 ? (
            <div className="hdrpanel-empty">Nothing saved yet. Tap the ♥ on any product and it lands here.</div>
          ) : (
            sc.saved.map((s) => <PanelRow key={s.id} item={s} onRemove={() => sc.removeSaved(s.id)} showLink />)
          )}
          {sc.saved.length ? (
            <Link className="pcta hdrpanel-cta" href="/saved">
              Open saved items →
            </Link>
          ) : null}
        </div>
      ) : null}

      {kind === 'cmp' ? (
        <div className="hdrpanel" role="dialog" aria-label="Compare list">
          <div className="hdrpanel-h">
            <span>
              Compare <span className="hdrpanel-n">{sc.compare.length}</span>
              <span className="hdrpanel-sub"> of {COMPARE_MAX}</span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
              {sc.compare.length ? (
                <button type="button" className="hdrpanel-clr" onClick={() => sc.clearCompare()}>
                  Clear all
                </button>
              ) : null}
              <button type="button" className="hdrpanel-x" onClick={onClose} aria-label="Close">
                ✕
              </button>
            </span>
          </div>
          {sc.compare.length === 0 ? (
            <div className="hdrpanel-empty">Nothing to compare yet. Tick “Compare” on up to four products.</div>
          ) : (
            sc.compare.map((c) => <PanelRow key={c.id} item={c} onRemove={() => sc.removeCompare(c.id)} showLink={false} />)
          )}
          {sc.compare.length ? (
            <Link className="pcta hdrpanel-cta" href={compareHref(sc.compare)}>
              Compare {sc.compare.length} {sc.compare.length === 1 ? 'model' : 'models'} side by side →
            </Link>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
